import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { Language } from '../../core/i18n/translations';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, IconComponent],
  template: `
    <div class="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#09090b] dark:text-slate-100 flex flex-col antialiased selection:bg-emerald-600 selection:text-white transition-colors duration-200">
      <header class="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur px-4 py-3 flex items-center justify-between shadow-xs">
        <div class="flex items-center space-x-2">
          <span class="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse"></span>
          <span class="text-xs font-bold tracking-wider uppercase text-slate-700 dark:text-slate-300">
            League of Managers
          </span>
        </div>

        <div class="relative">
          <button
            type="button"
            (click)="toggleSettingsMenu()"
            id="btn-verify-settings"
            [title]="game.t('nav.settings')"
            class="flex items-center space-x-1.5 p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer border border-slate-200 dark:border-slate-700/60"
          >
            <app-icon name="settings" [size]="16" />
          </button>

          @if (isSettingsMenuOpen()) {
            <div (click)="closeSettingsMenu()" class="fixed inset-0 z-40"></div>

            <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl py-2 z-50 text-xs transition-colors">
              <div class="px-3 py-1.5 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                {{ game.t('nav.settings') }}
              </div>

              <div class="p-1 space-y-0.5">
                <button
                  type="button"
                  (click)="game.toggleDarkMode()"
                  class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                >
                  <div class="flex items-center space-x-2.5">
                    @if (game.isDarkMode()) {
                      <app-icon name="sun" [size]="16" class="text-amber-400" />
                      <span class="font-medium">{{ game.t('nav.toggle_theme_light') }}</span>
                    } @else {
                      <app-icon name="moon" [size]="16" class="text-slate-600 dark:text-slate-300" />
                      <span class="font-medium">{{ game.t('nav.toggle_theme_dark') }}</span>
                    }
                  </div>
                  <div class="w-8 h-4.5 rounded-full p-0.5 transition-colors" [ngClass]="game.isDarkMode() ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'">
                    <div class="w-3.5 h-3.5 rounded-full bg-white transition-transform" [ngClass]="game.isDarkMode() ? 'translate-x-3.5' : 'translate-x-0'"></div>
                  </div>
                </button>

                <div class="pt-1 border-t border-slate-100 dark:border-slate-800">
                  <div class="px-3 py-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center justify-between">
                    <span class="flex items-center space-x-1.5">
                      <app-icon name="globe" [size]="12" />
                      <span>{{ game.t('nav.language') }}</span>
                    </span>
                    <span class="font-mono text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{{ game.i18n.currentLanguageOption().shortCode }}</span>
                  </div>

                  <div class="grid grid-cols-2 gap-1 px-1.5 py-1">
                    @for (lang of game.i18n.availableLanguages; track lang.code) {
                      <button
                        type="button"
                        (click)="selectLanguage(lang.code)"
                        class="flex items-center space-x-1.5 px-2 py-1.5 rounded-lg text-left cursor-pointer transition-colors"
                        [ngClass]="game.i18n.currentLanguage() === lang.code ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-200 dark:border-emerald-800/60' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'"
                      >
                        <span class="text-sm leading-none">{{ lang.flag }}</span>
                        <span class="text-[11px] truncate">{{ lang.nativeName }}</span>
                      </button>
                    }
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md space-y-6">
          <div class="text-center space-y-3">
            <img
              src="/icons/lom-192.png"
              alt="LOM"
              width="96"
              height="96"
              class="inline-block w-24 h-24 rounded-2xl object-contain shadow-md"
              referrerpolicy="no-referrer"
            />
            <h1 class="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              League of Managers
            </h1>
          </div>

          <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm dark:shadow-2xl transition-colors">
            @switch (viewState()) {
              @case ('ready') {
                <div class="text-center space-y-5">
                  <div class="w-14 h-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-xs">
                    <app-icon name="check-circle" [size]="28" />
                  </div>

                  <div class="space-y-1.5">
                    <h2 class="text-lg font-bold text-slate-900 dark:text-white" role="status">
                      {{ game.t('auth.verify_title') }}
                    </h2>
                    <p class="text-xs text-slate-500 dark:text-slate-400 px-2 leading-relaxed">
                      {{ game.t('auth.verify_subtitle') }}
                    </p>
                  </div>

                  <div class="pt-2">
                    <button
                      type="button"
                      id="btn-confirm-email"
                      (click)="confirmEmail()"
                      [disabled]="isConfirming()"
                      class="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-98"
                    >
                      @if (isConfirming()) {
                        <app-icon name="loader" [size]="16" class="animate-spin text-white" />
                        <span>{{ game.t('auth.verify_confirming') }}</span>
                      } @else {
                        <span>{{ game.t('auth.verify_btn') }}</span>
                        <app-icon name="arrow-right" [size]="16" />
                      }
                    </button>
                  </div>
                </div>
              }

              @case ('success') {
                <div class="text-center space-y-5" role="status">
                  <div class="w-14 h-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-xs">
                    <app-icon name="check-circle" [size]="28" />
                  </div>

                  <div class="space-y-1.5">
                    <h2 class="text-lg font-bold text-slate-900 dark:text-white">
                      {{ game.t('auth.verify_success_title') }}
                    </h2>
                    <p class="text-xs text-slate-600 dark:text-slate-300 px-2 leading-relaxed">
                      {{ game.t('auth.verify_success_desc') }}
                    </p>
                  </div>

                  <div class="pt-2">
                    <button
                      type="button"
                      (click)="goToLogin()"
                      [disabled]="isGoingToLogin()"
                      id="btn-verified-login"
                      class="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 transition-all shadow-xs cursor-pointer"
                    >
                      <span>{{ game.t('auth.verify_go_to_login') }}</span>
                      <app-icon name="arrow-right" [size]="16" />
                    </button>
                  </div>
                </div>
              }

              @case ('error') {
                <div class="space-y-5 text-center">
                  <div class="w-14 h-14 mx-auto rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center shadow-xs">
                    <app-icon name="alert-circle" [size]="28" />
                  </div>

                  <div class="space-y-1.5">
                    <h2 class="text-base font-bold text-slate-900 dark:text-white" role="alert">
                      {{ errorMessage() }}
                    </h2>
                  </div>

                  <!-- Resend request section if token expired/invalid -->
                  <div class="pt-3 border-t border-slate-100 dark:border-slate-800 text-left space-y-3">
                    <div>
                      <h3 class="text-xs font-bold text-slate-800 dark:text-slate-200">
                        {{ game.t('auth.verify_err_resend_title') }}
                      </h3>
                      <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {{ game.t('auth.verify_err_resend_desc') }}
                      </p>
                    </div>

                    @if (resendNotice()) {
                      <div class="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/60 rounded-xl text-xs text-emerald-800 dark:text-emerald-200" role="status">
                        {{ resendNotice() }}
                      </div>
                    }

                    <div class="space-y-2">
                      <input
                        type="email"
                        [(ngModel)]="resendEmail"
                        id="verify-resend-email-input"
                        [placeholder]="game.t('auth.verify_enter_email')"
                        class="block w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                      />

                      <button
                        type="button"
                        id="btn-request-new-link"
                        (click)="requestNewLink()"
                        [disabled]="!resendEmail.trim() || resendCooldown() > 0 || isResending()"
                        class="w-full flex items-center justify-center space-x-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        @if (isResending()) {
                          <app-icon name="loader" [size]="14" class="animate-spin text-white dark:text-slate-900" />
                          <span>{{ game.t('auth.resend_sending') }}</span>
                        } @else if (resendCooldown() > 0) {
                          <app-icon name="clock" [size]="14" />
                          <span>{{ game.t('auth.resend_wait', { seconds: resendCooldown() }) }}</span>
                        } @else {
                          <span>{{ game.t('auth.verify_request_new_link') }}</span>
                        }
                      </button>
                    </div>
                  </div>

                  <div class="pt-2">
                    <a
                      routerLink="/login"
                      [fragment]="pendingInvitationFragment"
                      class="text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                    >
                      {{ game.t('auth.google_back_to_login') }}
                    </a>
                  </div>
                </div>
              }
            }
          </div>
        </div>
      </main>

      <footer class="border-t border-slate-200 dark:border-slate-800 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
        <span class="font-mono text-[11px]">{{ game.appVersionTag }}</span>
      </footer>
    </div>
  `
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  readonly game = inject(GameService);

  readonly isSettingsMenuOpen = signal<boolean>(false);
  readonly viewState = signal<'ready' | 'success' | 'error'>('ready');
  readonly isConfirming = signal<boolean>(false);
  readonly isGoingToLogin = signal(false);
  readonly isResending = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly resendNotice = signal<string | null>(null);
  readonly resendCooldown = signal<number>(0);

  resendEmail = '';
  private memoryToken: string | null = null;
  private cooldownTimer: any = null;
  private metaReferrerCreated = false;

  get pendingInvitationFragment(): string | undefined {
    if (typeof sessionStorage !== 'undefined') {
      const saved = sessionStorage.getItem('lom_pending_invitation');
      if (saved) return `invitation=${saved}`;
    }
    return undefined;
  }

  ngOnInit(): void {
    this.ensureNoReferrerPolicy();

    // Read token strictly from window hash/fragment
    let tokenFound: string | null = null;
    if (typeof window !== 'undefined' && window.location.hash) {
      const rawHash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const params = new URLSearchParams(rawHash);
      tokenFound = params.get('token');
    }

    // Immediately remove fragment from URL/history to avoid leaking
    if (typeof history !== 'undefined' && typeof window !== 'undefined') {
      history.replaceState(null, '', window.location.pathname);
    }

    // Keep token strictly in memory
    this.memoryToken = tokenFound;

    if (!this.memoryToken || !this.memoryToken.trim()) {
      this.errorMessage.set(this.game.t('auth.verify_missing_token'));
      this.viewState.set('error');
    } else {
      this.viewState.set('ready');
    }
  }

  ngOnDestroy(): void {
    this.memoryToken = null;
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    if (this.metaReferrerCreated && typeof document !== 'undefined') {
      const meta = document.querySelector('meta[name="referrer"][data-verify="true"]');
      if (meta) meta.remove();
    }
  }

  private ensureNoReferrerPolicy(): void {
    if (typeof document === 'undefined') return;
    let meta = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = 'no-referrer';
      meta.setAttribute('data-verify', 'true');
      document.head.appendChild(meta);
      this.metaReferrerCreated = true;
    } else {
      meta.content = 'no-referrer';
    }
  }

  toggleSettingsMenu(): void {
    this.isSettingsMenuOpen.update((v) => !v);
  }

  closeSettingsMenu(): void {
    this.isSettingsMenuOpen.set(false);
  }

  selectLanguage(lang: Language): void {
    this.game.i18n.setLanguage(lang);
  }

  async goToLogin(): Promise<void> {
    if (this.isGoingToLogin()) return;
    this.isGoingToLogin.set(true);
    const fragment = this.pendingInvitationFragment;
    try {
      // Verification does not authenticate the recipient. End any previous
      // session before opening login, otherwise guestGuard restores that user.
      await this.game.auth.logout(false);
      await this.game.router.navigate(['/login'], { fragment, replaceUrl: true });
    } finally {
      this.isGoingToLogin.set(false);
    }
  }

  async confirmEmail(): Promise<void> {
    if (!this.memoryToken || this.isConfirming()) return;

    this.isConfirming.set(true);
    const token = this.memoryToken;
    this.memoryToken = null; // Clear token immediately after starting submission

    try {
      await this.game.verifyRegistration({ token });
      this.viewState.set('success');
    } catch (err: any) {
      if (err?.status === 400 && (err?.code === 'invalid_token' || err?.responseBody?.code === 'invalid_token')) {
        this.errorMessage.set(this.game.t('auth.verify_err_invalid_token'));
      } else if (err?.status === 429) {
        this.errorMessage.set(this.game.t('auth.err_rate_limited'));
      } else if (err?.status === 503) {
        this.errorMessage.set(this.game.t('auth.err_unavailable'));
      } else {
        this.errorMessage.set(this.game.t('auth.verify_err_invalid_token'));
      }
      this.viewState.set('error');
    } finally {
      this.isConfirming.set(false);
    }
  }

  async requestNewLink(): Promise<void> {
    const email = this.resendEmail.trim();
    if (!email || this.resendCooldown() > 0 || this.isResending()) return;

    this.isResending.set(true);
    this.resendNotice.set(null);
    try {
      await this.game.resendRegistration({ email });
      this.resendNotice.set(this.game.t('auth.resend_sent'));
      this.startResendCooldown(60);
    } catch (err: any) {
      if (err?.status === 429) {
        const cd = err.retryAfter || 60;
        this.startResendCooldown(cd);
        this.resendNotice.set(this.game.t('auth.err_rate_limited'));
      } else {
        this.resendNotice.set(this.game.t('auth.err_unavailable'));
      }
    } finally {
      this.isResending.set(false);
    }
  }

  private startResendCooldown(seconds: number): void {
    this.resendCooldown.set(seconds);
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown.update((v) => {
        if (v <= 1) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
          return 0;
        }
        return v - 1;
      });
    }, 1000);
  }
}
